import { Meal } from "@/clients/models/Meal";
import { MealTime } from "@/clients/models/MealTime";
import { UpdateMealRequest } from "@/clients/models/UpdateMealRequest";
import { CheckIcon } from "@/components/icons/check-icon";
import { FilledButton } from "@/components/ui/buttons";
import { LocalStorageKeys } from "@/constants/local-storage-keys";
import { QueryKeys } from "@/constants/query-keys";
import { useConfirmPlanMealSwap } from "@/hooks/requests/plan/use-confirm-plan-meal-swap";
import { useDeletePlanMealSwap } from "@/hooks/requests/plan/use-delete-plan-meal-swap";
import { useUpdatePlanMeal } from "@/hooks/requests/plan/use-update-plan-meal";
import { useToast } from "@/hooks/ui/use-toast";
import { cn } from "@/utils/cn";
import { useIonRouter } from "@ionic/react";
import { useQueryClient } from "@tanstack/react-query";
import { memo, useState } from "react";
import { SwapPlanAdjustmentModal } from "../swap-plan-adjustment-modal";

interface SwapPlanConfirmButtonProps {
  meal: Meal;
  mealTime: MealTime;
  mealDay: string;
  targetDate?: string;
  mealIndex?: number;
  currentMealId?: string;
  requestId?: string | null;
  /** Position/index of this meal in the alternatives list */
  selectedPosition?: number;
  className?: string;
  ariaLabel?: string;
  /**
   * True when the swap targets a meal in day_plan.extraMeals.
   * Sent to the backend via `isExtra`; also disables the Undo path
   * in the adjustment modal since undo is not supported for extras.
   */
  isExtra?: boolean;
}

export const SwapPlanConfirmButton = memo(function SwapPlanConfirmButton({
  meal,
  mealTime,
  mealDay,
  targetDate,
  mealIndex,
  currentMealId,
  requestId,
  selectedPosition,
  className,
  ariaLabel = "Confirm and add this meal to your plan",
  isExtra = false,
}: SwapPlanConfirmButtonProps) {
  const queryClient = useQueryClient();
  const navigation = useIonRouter();
  const { showError, showInfo } = useToast();
  const [isAdjustmentModalOpen, setIsAdjustmentModalOpen] = useState(false);
  const { updateMealAsync, isLoading: isUpdating } = useUpdatePlanMeal();
  const { confirmPlanMealSwapAsync, isLoading: isConfirming } = useConfirmPlanMealSwap();
  const { deletePlanMealSwapAsync, isLoading: isUndoing } = useDeletePlanMealSwap();

  const navigateBackToMyPlan = () => {
    const searchParamsJson = localStorage.getItem(LocalStorageKeys.SWAP_PLAN_SEARCH_PARAMS);
    const searchParams = searchParamsJson ? JSON.parse(searchParamsJson) : {};
    if (Object.keys(searchParams).length > 0) {
      const newSearchParams = new URLSearchParams(searchParams);
      navigation.push(`/plans/my-plan?${newSearchParams.toString()}`, "back", "push");
    } else {
      navigation.push("/plans/my-plan", "back", "push");
    }
  };

  const handlePickMeal = async () => {
    if (!meal?.id || !mealTime || !mealDay) {
      return;
    }

    const updateMealRequest: UpdateMealRequest = {
      day: mealDay,
      mealIndex: mealIndex,
      currentMealId: currentMealId ?? undefined,
      mealTime: mealTime,
      meal_id: meal.id ?? "",
      meal_name: meal.name,
      requestId: requestId ?? undefined,
      selectedPosition: selectedPosition ?? undefined,
      targetDate: targetDate ?? undefined,
      isExtra: isExtra || undefined,
    };

    try {
      const result = await updateMealAsync(updateMealRequest);
      const needsAdjustment = Boolean(result?.needs_adjustment);
      console.log("needsAdjustment", needsAdjustment);
      queryClient.removeQueries({
        queryKey: [QueryKeys.PLAN_ALTERNATIVE, meal.id, mealTime],
      });
      if (needsAdjustment) {
        setIsAdjustmentModalOpen(true);
        return;
      }
      localStorage.removeItem(LocalStorageKeys.SWAP_PLAN_MEAL_DATA);
      navigateBackToMyPlan();
      showInfo(`I’ve updated your ${mealTime}!`, { duration: 5000 });
    } catch (error) {
      if (error instanceof Error) {
        showError(error.message);
      } else {
        showError("An unknown error occurred: " + error);
      }
    }
  };

  const handleConfirmSwap = async () => {
    try {
      await confirmPlanMealSwapAsync({
        day: mealDay,
        mealTime,
        mealIndex,
      });
      setIsAdjustmentModalOpen(false);
      localStorage.removeItem(LocalStorageKeys.SWAP_PLAN_MEAL_DATA);
      navigateBackToMyPlan();
      showInfo("Swap confirmed", { duration: 5000 });
    } catch (error) {
      if (error instanceof Error) {
        showError(error.message);
      } else {
        showError("Failed to confirm swap");
      }
    }
  };

  const handleUndoSwap = async () => {
    try {
      await deletePlanMealSwapAsync({
        day: mealDay,
        mealId: meal.id ?? "",
        mealTime,
        mealIndex,
        targetDate: targetDate ?? undefined,
      });
      setIsAdjustmentModalOpen(false);
      showInfo("Swap undone", { duration: 5000 });
    } catch (error) {
      if (error instanceof Error) {
        showError(error.message);
      } else {
        showError("Failed to undo swap");
      }
    }
  };

  return (
    <>
      <FilledButton
        onClick={handlePickMeal}
        color="black"
        disabled={isUpdating}
        className={cn(
          "h-14 w-14 rounded-full",
          {
            "bg-purple-700 text-white shadow-lg hover:scale-105 hover:bg-purple-800 active:scale-95":
              !isUpdating,
            "cursor-not-allowed bg-purple-400 text-white opacity-50": isUpdating,
          },
          className,
        )}
        aria-label={ariaLabel}
      >
        <CheckIcon className="h-6 w-6 stroke-white" />
      </FilledButton>

      <SwapPlanAdjustmentModal
        isOpen={isAdjustmentModalOpen}
        onDismiss={() => setIsAdjustmentModalOpen(false)}
        onConfirm={handleConfirmSwap}
        onUndoSwap={handleUndoSwap}
        isConfirming={isConfirming}
        isUndoing={isUndoing}
        canUndo={!isExtra}
      />
    </>
  );
});
